/**
 * Recording what a person asked — by phone, in a letter, or passed on by a
 * client — on their behalf.
 *
 * The kind sets what it covers by default; staff may narrow or widen it. A
 * request is applied only once somebody has recorded how they checked it was
 * the person (unless its kind needs no check), and only by somebody who may
 * approve it — an erasure only by somebody who may also delete. Who may do
 * what is the server's; the page offers "Apply now" to those it would accept.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  ApiError,
  Button,
  Checkbox,
  Input,
  Modal,
  Select,
  Textarea,
  toast,
  useCan,
  useIdempotencyKey,
} from '@connect/web-shared';

import { ScopeEditor, useScopeState } from './scope-editor';
import { EMPTY_SCOPE, requestKeys, requestsApi, type CreateBody, type Settings } from './requests-api';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : undefined);

export function NewRequestDialog({
  open,
  onClose,
  settings,
}: {
  open: boolean;
  onClose: () => void;
  settings: Settings;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const keys = useIdempotencyKey();
  const canApprove = useCan('messaging.individual_request.approve');
  const canDelete = useCan('messaging.individual_request.delete');
  const kinds = settings.types.filter((t) => t.active);
  const [kindKey, setKindKey] = useState(kinds[0]?.key ?? '');
  const kind = kinds.find((t) => t.key === kindKey);
  const { scope, setScope, names, setNames } = useScopeState({
    ...EMPTY_SCOPE,
    categories: kind?.categories ?? [],
  });
  const [people, setPeople] = useState('');
  const [reason, setReason] = useState('');
  const [share, setShare] = useState(false);
  const [checked, setChecked] = useState('');
  const [note, setNote] = useState('');
  const [applyNow, setApplyNow] = useState(false);
  const [confirmOverride, setConfirmOverride] = useState(false);

  const mayApply = canApprove && (!kind?.erasure || canDelete);
  const body = useMemo<CreateBody>(
    () => ({
      type: kindKey,
      identifiers: people
        .split(/[\n,;]+/)
        .map((v) => v.trim())
        .filter(Boolean),
      scope,
      stated_reason: reason.trim() || undefined,
      share_reason: share,
      staff_note: note.trim() || undefined,
      verification_note: checked.trim() || undefined,
      apply_now: mayApply && applyNow,
      confirm_override: confirmOverride,
    }),
    [kindKey, people, scope, reason, share, note, checked, mayApply, applyNow, confirmOverride],
  );
  const create = useMutation({
    mutationFn: () => requestsApi.create(body, keys.keyFor(body)),
    onSuccess: async (made) => {
      keys.reset();
      toast.success(`${made.code} recorded`);
      onClose();
      await queryClient.invalidateQueries({ queryKey: requestKeys.all });
      navigate(`/individual-requests/${made.public_id}`);
    },
  });

  const chooseKind = (key: string) => {
    setKindKey(key);
    const next = kinds.find((t) => t.key === key);
    setScope({ ...EMPTY_SCOPE, categories: next?.categories ?? [] });
    setNames({});
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      title="Record a request"
      description="What a person asked about the messages Connect’s clients send them. Their numbers and addresses are kept as a keyed hash, shown masked."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={create.isPending}
            disabled={!kindKey || !body.identifiers.length}
            onClick={() => create.mutate()}
          >
            {body.apply_now ? 'Record and apply' : 'Record'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {create.isError ? <Alert tone="danger">{errorText(create.error) ?? 'Not recorded.'}</Alert> : null}
        <Select
          label="What they asked"
          value={kindKey}
          onChange={(event) => chooseKind(event.target.value)}
          options={kinds.map((t) => ({ value: t.key, label: t.label }))}
          hint={kind?.description}
        />
        <Textarea
          label="Their mobile numbers or email addresses"
          hint="One per line. Every one is theirs: the request covers each."
          rows={2}
          value={people}
          onChange={(event) => setPeople(event.target.value)}
        />
        {kind ? (
          <ScopeEditor
            value={scope}
            onChange={setScope}
            names={names}
            onNames={setNames}
            categories={settings.categories}
            allows={kind.effect === 'block'}
          />
        ) : null}
        <Textarea
          label="Their reason, in their words"
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Checkbox
          label="A business that keeps them may read this reason"
          hint="Leave it off where the reason names another business."
          checked={share}
          onChange={(event) => setShare(event.target.checked)}
        />
        <Input
          label="How you checked it is them"
          hint={
            kind?.requires_verification
              ? 'Needed before it can apply — a call back on the number, a signed letter.'
              : 'Optional for this kind of request.'
          }
          value={checked}
          maxLength={300}
          onChange={(event) => setChecked(event.target.value)}
        />
        <Textarea label="Note for staff" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        {mayApply ? (
          <Checkbox
            label="Apply it now"
            hint="Every send from the next one on obeys it."
            checked={applyNow}
            disabled={kind?.requires_verification && !checked.trim()}
            onChange={(event) => setApplyNow(event.target.checked)}
          />
        ) : null}
        {mayApply && applyNow && kind?.effect === 'allow' ? (
          <Checkbox
            label="The person asked for this exception"
            hint="It lets through messages they may have asked to stop."
            checked={confirmOverride}
            onChange={(event) => setConfirmOverride(event.target.checked)}
          />
        ) : null}
      </div>
    </Modal>
  );
}
